import { supabase } from '@/integrations/supabase/client'

interface DiagnosticResult {
  test: string
  success: boolean
  message: string
  details?: unknown
  fix?: string
}

/**
 * Diagnostic complet du système de blog
 */
export const runBlogDiagnostic = async (): Promise<{
  success: boolean
  results: DiagnosticResult[]
  summary: string
}> => {
  const results: DiagnosticResult[] = []
  let totalTests = 0
  let passedTests = 0

  console.log('🔍 Début du diagnostic du système de blog...')

  // Test 1: Connexion utilisateur
  console.log('👤 Test 1: Vérification de l\'authentification...')
  try {
    const { data: { user }, error } = await supabase.auth.getUser()
    if (error) throw error
    
    if (user) {
      results.push({
        test: 'Authentification',
        success: true,
        message: `Utilisateur connecté: ${user.email}`,
        details: { userId: user.id }
      })
      passedTests++
    } else {
      results.push({
        test: 'Authentification',
        success: false,
        message: 'Aucun utilisateur connecté',
        fix: 'Connectez-vous via la page /admin/login'
      })
    }
    totalTests++
  } catch (error) {
    results.push({
      test: 'Authentification',
      success: false,
      message: `Erreur d'authentification: ${error instanceof Error ? error.message : 'Erreur inconnue'}`,
      details: error
    })
    totalTests++
  }

  // Test 2: Accès à la table blog_posts
  console.log('🗄️ Test 2: Accès à la table blog_posts...')
  try {
    const { data, error } = await supabase
      .from('blog_posts')
      .select('id, title, status')
      .limit(1)
    
    if (error) throw error
    
    results.push({
      test: 'Accès table blog_posts',
      success: true,
      message: 'Table accessible en lecture',
      details: { articleCount: data?.length || 0 }
    })
    passedTests++
    totalTests++
  } catch (error) {
    results.push({
      test: 'Accès table blog_posts',
      success: false,
      message: `Impossible d'accéder à la table: ${error instanceof Error ? error.message : 'Erreur inconnue'}`,
      details: error,
      fix: 'Vérifiez que la table blog_posts existe et que les politiques RLS permettent la lecture'
    })
    totalTests++
  }

  // Test 3: Test d'insertion (si utilisateur connecté)
  console.log('📝 Test 3: Test d\'insertion d\'article...')
  try {
    const { data: { user } } = await supabase.auth.getUser()
    
    if (user) {
      const testArticle = {
        title: 'Test Article ' + Date.now(),
        slug: 'test-article-' + Date.now(),
        excerpt: 'Test excerpt pour diagnostic',
        content: 'Contenu de test pour vérifier l\'insertion',
        image_url: '',
        category: 'Test',
        tags: 'diagnostic, test',
        status: 'draft' as const,
        author_id: user.id,
        read_time: 1
      }

      const { data, error } = await supabase
        .from('blog_posts')
        .insert(testArticle)
        .select()
        .single()

      if (error) throw error

      // Nettoyer immédiatement
      await supabase.from('blog_posts').delete().eq('id', data.id)

      results.push({
        test: 'Insertion article',
        success: true,
        message: 'Insertion et suppression réussies',
        details: { testId: data.id }
      })
      passedTests++
    } else {
      results.push({
        test: 'Insertion article',
        success: false,
        message: 'Test ignoré - utilisateur non connecté',
        fix: 'Connectez-vous pour tester l\'insertion'
      })
    }
    totalTests++
  } catch (error) {
    results.push({
      test: 'Insertion article',
      success: false,
      message: `Insertion échouée: ${error instanceof Error ? error.message : 'Erreur inconnue'}`,
      details: error,
      fix: 'Problème de politiques RLS ou de permissions. Exécutez le script fix-rls.sql'
    })
    totalTests++
  }

  // Test 4: Bucket de stockage
  console.log('🖼️ Test 4: Vérification du bucket de stockage...')
  try {
    const { data: buckets, error } = await supabase.storage.listBuckets()
    if (error) throw error

    const blogImagesBucket = buckets?.find(bucket => bucket.name === 'blog-images')
    
    if (blogImagesBucket) {
      results.push({
        test: 'Bucket blog-images',
        success: true,
        message: 'Bucket existe et est accessible',
        details: blogImagesBucket
      })
      passedTests++
    } else {
      results.push({
        test: 'Bucket blog-images',
        success: false,
        message: 'Bucket blog-images introuvable',
        fix: 'Créez le bucket via le dashboard Supabase ou exécutez la migration'
      })
    }
    totalTests++
  } catch (error) {
    results.push({
      test: 'Bucket blog-images',
      success: false,
      message: `Erreur d'accès au storage: ${error instanceof Error ? error.message : 'Erreur inconnue'}`,
      details: error
    })
    totalTests++
  }

  // Test 5: Test d'upload
  console.log('⬆️ Test 5: Test d\'upload d\'image...')
  try {
    const testBlob = new Blob(['test image content'], { type: 'text/plain' })
    const testFile = new File([testBlob], 'test-diagnostic.txt', { type: 'text/plain' })
    
    const { data, error } = await supabase.storage
      .from('blog-images')
      .upload(`diagnostic/${Date.now()}.txt`, testFile)
    
    if (error) throw error
    
    // Nettoyer
    await supabase.storage.from('blog-images').remove([data.path])
    
    results.push({
      test: 'Upload image',
      success: true,
      message: 'Upload et suppression réussis',
      details: { path: data.path }
    })
    passedTests++
    totalTests++
  } catch (error) {
    results.push({
      test: 'Upload image',
      success: false,
      message: `Upload échoué: ${error instanceof Error ? error.message : 'Erreur inconnue'}`,
      details: error,
      fix: 'Vérifiez les politiques du bucket blog-images'
    })
    totalTests++
  }

  // Test 6: Table admin_users
  console.log('👮 Test 6: Vérification des permissions admin...')
  try {
    const { data: { user } } = await supabase.auth.getUser()
    
    if (user) {
      const { data, error } = await supabase
        .from('admin_users')
        .select('id, email')
        .eq('user_id', user.id)
        .single()
      
      if (error && !error.message.includes('No rows')) throw error
      
      if (data) {
        results.push({
          test: 'Permissions admin',
          success: true,
          message: 'Utilisateur configuré comme admin',
          details: data
        })
        passedTests++
      } else {
        results.push({
          test: 'Permissions admin',
          success: false,
          message: 'Utilisateur non configuré comme admin',
          fix: 'Ajoutez votre email à la table admin_users'
        })
      }
    } else {
      results.push({
        test: 'Permissions admin',
        success: false,
        message: 'Test ignoré - utilisateur non connecté'
      })
    }
    totalTests++
  } catch (error) {
    results.push({
      test: 'Permissions admin',
      success: false,
      message: `Erreur vérification admin: ${error instanceof Error ? error.message : 'Erreur inconnue'}`,
      details: error
    })
    totalTests++
  }

  console.log(`✅ Diagnostic terminé: ${passedTests}/${totalTests} tests réussis`)

  const success = passedTests === totalTests
  const summary = `Diagnostic: ${passedTests}/${totalTests} tests réussis. ${
    success ? 'Système fonctionnel ✅' : 'Problèmes détectés ⚠️'
  }`

  return {
    success,
    results,
    summary
  }
}

/**
 * Correction rapide des problèmes courants
 */
export const quickFixBlogIssues = async (): Promise<{
  success: boolean
  results: DiagnosticResult[]
  message: string
}> => {
  const results: DiagnosticResult[] = []
  
  console.log('🔧 Début de la correction rapide...')

  try {
    // 1. Créer le bucket s'il n'existe pas
    console.log('🪣 Création du bucket blog-images...')
    try {
      const { data: buckets } = await supabase.storage.listBuckets()
      const bucketExists = buckets?.some(bucket => bucket.name === 'blog-images')
      
      if (!bucketExists) {
        const { error } = await supabase.storage.createBucket('blog-images', {
          public: true,
          allowedMimeTypes: ['image/*'],
          fileSizeLimit: 10485760 // 10MB
        })
        
        if (error) throw error
        
        results.push({
          test: 'Création bucket',
          success: true,
          message: 'Bucket blog-images créé avec succès'
        })
      } else {
        results.push({
          test: 'Création bucket',
          success: true,
          message: 'Bucket blog-images existe déjà'
        })
      }
    } catch (error) {
      results.push({
        test: 'Création bucket',
        success: false,
        message: `Échec création bucket: ${error instanceof Error ? error.message : 'Erreur inconnue'}`
      })
    }

    // 2. Configurer l'utilisateur comme admin
    console.log('👤 Configuration utilisateur admin...')
    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      if (user) {
        const { error } = await supabase
          .from('admin_users')
          .upsert({
            user_id: user.id,
            email: user.email || 'admin@example.com'
          }, {
            onConflict: 'email'
          })
        
        if (error && !error.message.includes('duplicate')) throw error
        
        results.push({
          test: 'Configuration admin',
          success: true,
          message: 'Utilisateur configuré comme admin'
        })
      } else {
        results.push({
          test: 'Configuration admin',
          success: false,
          message: 'Aucun utilisateur connecté'
        })
      }
    } catch (error) {
      results.push({
        test: 'Configuration admin',
        success: false,
        message: `Échec configuration admin: ${error instanceof Error ? error.message : 'Erreur inconnue'}`
      })
    }

    return {
      success: true,
      results,
      message: 'Correction rapide terminée. Vérifiez les résultats ci-dessus.'
    }
    
  } catch (error) {
    return {
      success: false,
      results,
      message: `Erreur lors de la correction: ${error instanceof Error ? error.message : 'Erreur inconnue'}`
    }
  }
} 